import { hasStoredSession } from '@/utils/tokenStorage'
import { isStandaloneDisplay, webAuthnAvailable } from '@/utils/pwaDisplay'
import { clearDurableFlag, readDurableFlag, writeDurableFlag } from '@/utils/durableSession'
import { isFaceIdUserAgent, isPhoneUserAgent } from '@/utils/phoneUa'

const ENROLLED_KEY = 'sv.webauthn.enrolled'
const SNOOZE_KEY = 'sv.webauthn.snooze'
const OFFER_KEY = 'sv.webauthn.offer'
const SKIP_KEY = 'sv.skip_faceid'
const COOKIE = 'sv_passkey'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 400

function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function ssGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function ssSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* ignore */
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const s = part.trim()
    if (s.startsWith(prefix)) return decodeURIComponent(s.slice(prefix.length))
  }
  return null
}

function writePasskeyCookie(): void {
  if (typeof document === 'undefined' || window.location.protocol !== 'https:') return
  document.cookie = `${COOKIE}=1;Path=/;SameSite=Lax;Max-Age=${COOKIE_MAX_AGE};Secure`
}

function clearPasskeyCookie(): void {
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE}=;Path=/;Max-Age=0;SameSite=Lax`
  document.cookie = `${COOKIE}=;Path=/;Max-Age=0;SameSite=Lax;Secure`
}

export function markPasskeyEnrolled(): Promise<void> {
  lsSet(ENROLLED_KEY, '1')
  writePasskeyCookie()
  return writeDurableFlag(ENROLLED_KEY, '1')
}

export async function clearPasskeyEnrolled(): Promise<void> {
  lsRemove(ENROLLED_KEY)
  clearPasskeyCookie()
  await clearDurableFlag(ENROLLED_KEY)
}

export function isPasskeyEnrolledSync(): boolean {
  if (lsGet(ENROLLED_KEY) === '1') return true
  return readCookie(COOKIE) === '1'
}

export async function isPasskeyEnrolled(): Promise<boolean> {
  if (isPasskeyEnrolledSync()) return true
  const stored = await readDurableFlag(ENROLLED_KEY)
  if (stored === '1') {
    markPasskeyEnrolled()
    return true
  }
  return false
}

export async function restorePasskeyFlag(): Promise<void> {
  await isPasskeyEnrolled()
}

export function snoozePasskeyEnroll(): void {
  ssSet(SNOOZE_KEY, '1')
}

export function isPasskeyEnrollSnoozed(): boolean {
  return ssGet(SNOOZE_KEY) === '1'
}

export function skipAutoFaceId(): void {
  ssSet(SKIP_KEY, '1')
}

export function isFaceIdClient(ua?: string): boolean {
  const agent = ua ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')
  return isFaceIdUserAgent(agent) || isPhoneUserAgent(agent)
}

export function armPasskeyOffer(): void {
  ssSet(OFFER_KEY, '1')
  try {
    sessionStorage.removeItem(SNOOZE_KEY)
  } catch {
    /* ignore */
  }
}

export function clearPasskeyOffer(): void {
  try {
    sessionStorage.removeItem(OFFER_KEY)
  } catch {
    /* ignore */
  }
}

export function isPasskeyOfferArmed(): boolean {
  return ssGet(OFFER_KEY) === '1'
}

export function shouldOfferPasskeyEnroll(input: {
  pending: boolean
  authenticated: boolean
  webauthn: boolean
  enrolled: boolean
  prompted: boolean
  phone: boolean
  standalone: boolean
  snoozed: boolean
  blocked: boolean
  armed?: boolean
}): boolean {
  if (!input.authenticated || !input.webauthn || input.blocked || input.enrolled) return false
  if (input.armed) return true
  if (input.snoozed) return false
  if (input.standalone || input.phone) return true
  return input.pending && !input.prompted
}

export async function shouldAutoUnlock(): Promise<boolean> {
  if (!isStandaloneDisplay() || !webAuthnAvailable()) return false
  if (isFaceIdClient()) return false
  if (hasStoredSession()) return false
  if (ssGet(SKIP_KEY) === '1') return false
  return isPasskeyEnrolled()
}
